
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useAdminStatus } from "@/hooks/useAdminStatus";

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
}

export const ProtectedRoute = ({ children, adminOnly = false }: ProtectedRouteProps) => {
  const { user, isAdmin, isLoading } = useAdminStatus();
  const navigate = useNavigate();
  const authCheckTimeout = useRef<number | null>(null);

  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        navigate('/', { replace: true });
      } else if (adminOnly && !isAdmin) {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, isAdmin, isLoading, navigate, adminOnly]);

  // Show a loading state until auth check completes
  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-center min-h-screen"
      >
        <LoadingSpinner size="lg" />
      </motion.div>
    );
  }

  if (!user || (adminOnly && !isAdmin)) {
    return null; // The navigate in useEffect will handle redirection
  }

  return <>{children}</>;
};
